
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';

interface LinkInsertDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialUrl?: string;
  initialNewTab?: boolean;
  initialNofollow?: boolean;
  onInsert: (opts: { url: string; newTab: boolean; nofollow: boolean }) => void;
  onRemove?: () => void;
}

export const LinkInsertDialog: React.FC<LinkInsertDialogProps> = ({
  open,
  onOpenChange,
  initialUrl = '',
  initialNewTab = false,
  initialNofollow = false,
  onInsert,
  onRemove,
}) => {
  const [url, setUrl] = useState<string>(initialUrl);
  const [newTab, setNewTab] = useState<boolean>(initialNewTab);
  const [nofollow, setNofollow] = useState<boolean>(initialNofollow);

  React.useEffect(() => {
    // Sync when dialog opens with new initial values
    if (open) {
      setUrl(initialUrl);
      setNewTab(initialNewTab);
      setNofollow(initialNofollow);
    }
  }, [open, initialUrl, initialNewTab, initialNofollow]);

  const handleConfirm = () => {
    if (!url) return;
    onInsert({ url, newTab, nofollow });
    onOpenChange(false);
  };

  const handleRemove = () => {
    onRemove?.();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Insert link</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="link-url">URL</Label>
            <Input
              id="link-url"
              type="url"
              placeholder="https://example.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              autoFocus
            />
          </div>
          <div className="flex items-center gap-4 text-sm">
            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                className="accent-[hsl(var(--primary))]"
                checked={newTab}
                onChange={(e) => setNewTab(e.target.checked)}
              />
              <span>Open in new tab</span>
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                className="accent-[hsl(var(--primary))]"
                checked={nofollow}
                onChange={(e) => setNofollow(e.target.checked)}
              />
              <span>Rel nofollow</span>
            </label>
          </div>
        </div>
        <DialogFooter className="flex items-center justify-between">
          {onRemove && (
            <Button type="button" variant="outline" onClick={handleRemove}>
              Remove link
            </Button>
          )}
          <div className="ml-auto flex gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={handleConfirm}>
              {initialUrl ? 'Update' : 'Insert'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
